import React, { useEffect } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import SideDrawer from '../_custom/SideDrawer/SideDrawer';
import { StatusTimeline, StatusTimelineEntry, StatusTimelineViewedEvent } from '../../types/statusTimeline';
import { Mixpanel } from '../../utils/mixpanel';
import { useAppSelector } from '../../app/hooks';
import { calculateTimeSpent } from '../../utils/dateUtils';
import styles from './StatusTimelineDrawer.module.scss';
import ClockIcon from '../../assets/Svg/ClockIcon';

dayjs.extend(relativeTime);

interface StatusTimelineDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    statusTimeline: StatusTimeline | null;
    loading?: boolean;
}

const StatusTimelineDrawer: React.FC<StatusTimelineDrawerProps> = ({ isOpen, onClose, statusTimeline, loading = false }) => {
    const userData = useAppSelector((state) => state.user.userData);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const userList = useAppSelector((state) => state.investor.ownerDropdownOptions);

    const fetchUserName = (userId: string): string => {
        if (!userId || userList.length === 0) return 'SYSTEM';
        const user = userList.find((u) => u.userId === userId);
        return user?.name ?? 'SYSTEM';
    };

    useEffect(() => {
        if (isOpen && statusTimeline) {
            // Track Mixpanel event for timeline viewed
            const mixpanelEvent: StatusTimelineViewedEvent = {
                page: statusTimeline.recordType === 'LEAD' ? 'Leads' : statusTimeline.recordType === 'DEAL' ? 'Deals' : 'Mandate',
                source: userData?.accountType === 'PARTNER' ? 'partner' : 'pno',
                org_id: statusTimeline.recordId,
                user_email: userData?.primaryEmail || '',
                currentUrl: window.location.href,
                pno_id: platformData?.investorOrgId || '',
            };

            Mixpanel.track('status_timeline_viewed', mixpanelEvent);
        }
    }, [isOpen, statusTimeline, userData, platformData]);

    const getStatusTypeLabel = (recordType: string): string => {
        switch (recordType) {
            case 'LEAD':
                return 'Lead Status';
            case 'DEAL':
                return 'Deal Status';
            case 'MANDATE':
                return 'Mandate Status';
            default:
                return 'Status';
        }
    };

    const renderTimelineEntry = (entry: StatusTimelineEntry, index: number, isLast: boolean) => {
        // Calculate time spent from previous entry
        const nextEntry = !isLast ? statusTimeline?.timeline[index + 1] : undefined;
        const timeSpent = calculateTimeSpent(entry.enteredAt, nextEntry?.enteredAt);
        const formattedTime = dayjs(entry.enteredAt).format('h:mm a, D MMM YYYY');

        return (
            <div key={`${entry.enteredAt}-${index}`} className={styles.TimelineEntry}>
                <div className={styles.TimelineLeft}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div className={styles.StatusBadge} style={{ backgroundColor: entry.statusColor, color: entry.statusLabelColor }}>
                            {entry.statusName}
                        </div>
                        <div className={styles.TimeSpent}>
                            <ClockIcon width="14" height="14" />
                            {timeSpent}
                        </div>
                    </div>
                </div>

                <div className={styles.TimelineRight}>
                    <div className={styles.StatusTime}>
                        {formattedTime} by {fetchUserName(entry.userId ?? '')}
                    </div>
                </div>

                {!isLast && <div className={styles.TimelineConnector} />}
            </div>
        );
    };

    if (loading) {
        return (
            <SideDrawer width="40%" onClose={onClose}>
                <div className={styles.DrawerHeader}>
                    <h2 className={styles.DrawerTitle}>Loading...</h2>
                </div>
                <div className={styles.LoadingContainer}>
                    <div className={styles.LoadingSpinner} />
                </div>
            </SideDrawer>
        );
    }

    if (!statusTimeline) {
        return null;
    }

    return (
        <SideDrawer width="40%" onClose={onClose}>
            <div className={styles.DrawerHeader}>
                <h2 className={styles.DrawerTitle}>{getStatusTypeLabel(statusTimeline.recordType)}</h2>
                <p className={styles.CompanyName}>{statusTimeline.companyName}</p>
            </div>

            <div className={styles.DrawerContent}>
                <div className={styles.Timeline}>
                    {statusTimeline.timeline.map((entry, index) =>
                        renderTimelineEntry(entry, index, index === statusTimeline.timeline.length - 1),
                    )}
                </div>
            </div>
        </SideDrawer>
    );
};

export default StatusTimelineDrawer;
